import { unfreezeAppointment } from "@src/services";
import ClientLogsService from "@src/services/client-logs.service";
import { useEffect, useRef } from "react";

interface UseReleaseFrozenAppointmentOnMountOptions {
  userUuid?: string;
  hasFrozenAppointment: boolean;
  logContext: string;
  onReleased: () => void;
  onSettled?: () => void;
}

/**
 * Releases a previously frozen appointment exactly once when the component mounts.
 *
 * The hook snapshots the mount-time inputs so later context updates do not change
 * whether the release runs for the current visit.
 */
export const useReleaseFrozenAppointmentOnMount = ({
  userUuid,
  hasFrozenAppointment,
  logContext,
  onReleased,
  onSettled,
}: UseReleaseFrozenAppointmentOnMountOptions): void => {
  const hasAttemptedRef = useRef(false);
  const initialStateRef = useRef({ userUuid, hasFrozenAppointment });
  const onReleasedRef = useRef(onReleased);
  const onSettledRef = useRef(onSettled);

  onReleasedRef.current = onReleased;
  onSettledRef.current = onSettled;

  useEffect(() => {
    if (hasAttemptedRef.current) {
      return;
    }
    hasAttemptedRef.current = true;

    const { userUuid: initialUserUuid, hasFrozenAppointment: initialHasFrozenAppointment } =
      initialStateRef.current;

    if (!initialHasFrozenAppointment || !initialUserUuid) {
      onSettledRef.current?.();
      return;
    }

    ClientLogsService.logWebAction(
      initialUserUuid,
      logContext,
      "Unfreezing existing appointment",
      undefined,
      {},
    );

    unfreezeAppointment(initialUserUuid)
      .then((response) => {
        if (response && typeof response === "object" && "error" in response) {
          return;
        }
        onReleasedRef.current();
      })
      .catch(() => undefined)
      .finally(() => {
        onSettledRef.current?.();
      });
  }, [logContext]);
};
